import GraphSection from "@/components/blocks/marketplace/GraphSection";
import Filters from "@/components/Marketplace/Filters";
import Header from "@/components/Marketplace/headerMarketplace";
import Footer from "@/components/Marketplace/Footer";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { usePathname } from "next/navigation";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "next-i18next";
import { Card, CardContent } from "@/components/ui/card";
import Loader from "@/components/Loader";
import Layout from "@/components/blocks/layout/Layout";
import Meta from "@/components/blocks/layout/Meta";
import AuthorsData from "@/components/Marketplace/AuthorsData";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Link from "next/link";
import {
  usePostMarketPlaceFilterAuthors,
  usePostMarketplaceFilterStrategies,
} from "@/api/marketPlaceApi";
import { useUserContext } from "@/contexts/userContext";
import { useGetCategories, useGetLogos } from "@/api/strategyApi";
import { Button } from "@/components/ui/button";
import clsx from "clsx";
import { IoCloseCircle } from "react-icons/io5";
import AuthorLeaderboard from "@/components/blocks/marketplace/AuthorLeaderboard";
import StrategiesMarketplace from "@/components/blocks/marketplace/StrategiesMarketplace";

type CategoryType = {
  id: number;
  label: string;
  description: string;
  is_valid: "0" | "1"; // assuming "is_valid" is a stringified boolean
};

const defaultCategories: CategoryType[] = [
  {
    id: 1,
    label: "Volatility",
    description: "Indicators that measure price fluctuations or variability.",
    is_valid: "1",
  },
  {
    id: 2,
    label: "Overlap",
    description:
      "Indicators that are plotted on top of the price data, like moving averages.",
    is_valid: "1",
  },
  {
    id: 3,
    label: "Momentum",
    description: "Indicators that show the speed of price movement.",
    is_valid: "1",
  },
  {
    id: 4,
    label: "Volume",
    description: "Indicators based on trading volume data.",
    is_valid: "1",
  },
  {
    id: 5,
    label: "Trend",
    description: "Indicators that help identify the direction of the market.",
    is_valid: "1",
  },
  {
    id: 6,
    label: "Statistics",
    description: "Statistical-based indicators derived from price data.",
    is_valid: "1",
  },
  {
    id: 7,
    label: "Cycles",
    description:
      "Indicators that attempt to identify repeating price patterns.",
    is_valid: "1",
  },
  {
    id: 8,
    label: "High Volume",
    description:
      "Indicators that attempt to identify repeating price patterns.",
    is_valid: "1",
  },
];
const dataSize = 20;

function Marketplace() {
  const { darkTheme } = useDarkTheme();
  let [page, setPage] = useState(1);
  let [selectedTab, setSelectedTab] = useState("strategies");
  let [pageAuthors, setPageAuthors] = useState(1);
  let [pages, setPages] = useState(0);
  let [totalDataLength, setTotalDataLength] = useState(0);
  let [totalDataLengthAuthors, setTotalDataLengthAuthors] = useState(0);
  let [data, setData] = useState([]);
  let [dataSuggestions, setDataSuggestions] = useState([]);
  let [symbols, setSymbols] = useState([]);
  let [dataAuthors, setDataAuthors] = useState([]);
  let [loading, setLoading] = useState<boolean>(false);
  const [logos, setLogos] = useState<any>(new Map());
  const routerName = useRouter();
  const router = usePathname();
  let [currentPath, setCurrentPath] = useState("");
  const { t } = useTranslation("marketplace-page");
  const urlRouter = useRouter();
  const [urlSymbol, setUrlSymbol] = useState<string | undefined>(undefined);
  const { slug } = urlRouter.query;
  const { user } = useUserContext();
  const [categories, setCategories] =
    useState<CategoryType[]>(defaultCategories);
  const [activeCategorie, setActiveCategorie] = useState<
    CategoryType | undefined
  >();
  const { data: categoriesData } = useGetCategories();

  const [filters, setFilters]: any = useState({
    sort_order: "",
    win_p: "",
    SR: "",
    copiers: "",
    timeframe: "",
    sl_range: "",
    tp_range: "",
    exit: "",
    action: "",
    indicator_category: "",
    asset_class: "",
    symbol: Array.isArray(slug) ? slug[0] : slug ?? "",
  });
  const [filtersAuthors, setFiltersAuthors]: any = useState({
    sort_order: "win_p_desc",
    total_strategies: "",
    copiers: "",
    trading_profile: "",
    win_p: "",
  });

  const {
    data: filterStrategiesData,
    isSuccess: isSuccessFilterStrategies,
    refetch: refetchStrategiesData,
  } = usePostMarketplaceFilterStrategies(
    filters,
    String(page),
    String(dataSize),
  );
  const { mutateAsync: getLogo, data: logoData } = useGetLogos();

  const {
    data: filterAuthorData,
    isLoading: isLoadingAuthors,
    isSuccess: isSuccessAuthor,
    refetch: refetchAuthorData,
  } = usePostMarketPlaceFilterAuthors(
    filtersAuthors,
    String(pageAuthors),
    String(dataSize),
  );

  const toggleCategories = (category: CategoryType) => {
    setActiveCategorie((prev) => {
      const isSame = prev?.id === category.id;
      const newCategory = isSame ? undefined : category;

      // Update filters state
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        indicator_category: newCategory?.label ?? "", // Set label or clear
      }));

      return newCategory;
    });
  };

  useEffect(() => {
    if (slug) {
      setUrlSymbol(Array.isArray(slug) ? slug[0] : slug);
    } else {
      setUrlSymbol(undefined);
    }
  }, [slug]);

  useEffect(() => {
    const { showtraders } = routerName?.query;
    if (showtraders) {
      setSelectedTab("traders");
    }
  }, []);
  useEffect(() => {
    if (categoriesData?.data) {
      setCategories(categoriesData?.data);
    }
  }, [categoriesData]);

  useEffect(() => {
    setCurrentPath(router);
  }, [router]);

  useEffect(() => {
    if (isSuccessFilterStrategies && filterStrategiesData) {
      setDataSuggestions(filterStrategiesData?.data.items);
      setData(filterStrategiesData.data.items);
      if (dataSuggestions.length == 0) {
        setDataSuggestions(filterStrategiesData.data.items.slice(0, 5));
      }
      setTotalDataLength(filterStrategiesData.data.total);
      setPages(filterStrategiesData.data.pages);
      if (filterStrategiesData?.data) {
        let symbols = filterStrategiesData?.data?.items;
        let finalData: any = symbols.map((each: any) => each?.strategy?.symbol);
        setSymbols(finalData);
        getLogo({ symbols: finalData });
      }
    }
    setLoading(false);
  }, [filterStrategiesData, isSuccessFilterStrategies]);

  useEffect(() => {
    if (isSuccessAuthor && filterAuthorData) {
      setDataAuthors(filterAuthorData.data.items);
      setTotalDataLengthAuthors(filterAuthorData.data.total);
    }
    setLoading(false);
  }, [filterAuthorData, isSuccessAuthor]);

  // Trigger API call when filters or page change
  useEffect(() => {
    if (currentPath) {
      setLoading(true);
    }
  }, [filters, page, currentPath]);

  useEffect(() => {
    if (logoData) {
      setLogos((prev: {}) => ({
        ...prev,
        ...logoData?.data?.data, // merge new logo data into existing
      }));
    }
  }, [logoData]);

  return (
    <div className="w-full  pb-0">
      <div
        className={` px-4 sm:px-auto ${
          !user ? "ml-[2%] mr-[2%] " : ""
        } mt-[20px] flex flex-col items-center`}
      >
        {/* graph section */}
        <Card className="flex flex-col  lg:flex-row w-full   lg:h-[310px] h-auto p-4">
          <CardContent className="w-[100%] lg:w-[68%] p-0">
            <GraphSection />
          </CardContent>
          <CardContent className="w-full lg:w-[32%] lg:h-full   mt-5 lg:mt-0  p-0">
            <AuthorLeaderboard
              filtersAuthors={filtersAuthors}
              setFiltersAuthors={setFiltersAuthors}
              data={dataAuthors}
              logos={new Map()}
              loadingAuthors={isLoadingAuthors}
              darkTheme={darkTheme}
              pageAuthors={pageAuthors}
              setPageAuthors={setPageAuthors}
              totalDataLengthAuthors={totalDataLengthAuthors}
              t={t}
            />
          </CardContent>
        </Card>

        {/* options plus sort options */}
        <div className="m-5 "></div>
        <div className="w-full lg:flex-row flex-col lg:w-[100%] 2xl:w-[85%] mb-4 flex items-center gap-7">
          <Tabs
            onValueChange={(val) => {
              setSelectedTab(val);
            }}
            value={selectedTab}
            className="w-full  sm:max-w-[27%] sm:min-w-[260px]"
          >
            <TabsList className="w-full">
              <TabsTrigger value={"strategies"} className="!grow w-full">
                {t("Top Strategies")}
              </TabsTrigger>
              <TabsTrigger value={"traders"} className="!grow w-full">
                {t("Top Traders")}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="overflow-scroll">
            {selectedTab == "strategies" ? (
              <div className="flex flex-row gap-2 p-4 h-20 overflow-x-auto">
                {categories.map((category) => (
                  <Button
                    variant={"outline"}
                    key={category.id}
                    onClick={() => toggleCategories(category)}
                    className={clsx(
                      "rounded-full text-xs border-[0.15rem] border-[#545454]/80  ",
                      activeCategorie?.id == category.id &&
                        filters.indicator_category == category.label &&
                        "bg-blue-600 text-white border-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600"
                    )}
                  >
                    {t(category.label)}
                    {activeCategorie?.id == category.id &&
                      filters.indicator_category == category.label && (
                        <IoCloseCircle />
                      )}
                  </Button>
                ))}
              </div>
            ) : (
              <div className="p-10"></div>
            )}
          </div>

          {urlSymbol && (
            <div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <Link href="/marketplace" passHref>
                      <BreadcrumbLink>Marketplace</BreadcrumbLink>
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />

                  <BreadcrumbItem>
                    <BreadcrumbPage>{urlSymbol}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          )}
        </div>

        <div
          className={`w-full lg:w-[100%]  h-auto md:h-[80vh]  2xl:w-[85%] flex flex-col sm:flex-row justify-between  ${
            selectedTab == "strategies" ? "flex" : "hidden"
          }`}
        >
          {/* filters */}
          <div className="w-[100%]  top-0  overflow-auto sm:w-[27%] h-full md:h-auto">
            <Filters
              filters={filters}
              setFilters={setFilters}
              symbol={currentPath}
              setCurrentPath={setCurrentPath}
              currentPath={currentPath}
            />
          </div>

          {/* profiles */}
          <Card className="w-full  mt-5 sm:mt-0 sm:w-[71%] overflow-auto h-full ">
            {loading || data.length == 0 ? (
              !loading ? (
                <div className="w-full flex flex-col items-center justify-center mt-[20px] text-xs text-muted-foreground">
                  {/* No data found  */}
                  {filters?.symbol || router?.split("/")[2]
                    ? `No strategies for ${
                        filters.symbol || router?.split("/")[2]
                      } found`
                    : "No data found"}
                  <div className="w-[75%]  mt-[60px] border-top border-gray border-[1px]"></div>
                </div>
              ) : (
                <div className="w-full flex items-center justify-center h-[200px]">
                  <Loader />
                </div>
              )
            ) : (
              <StrategiesMarketplace
                data={data}
                logos={logos}
                totalDataLength={totalDataLength}
                page={page}
                totalPages={pages}
                setPage={setPage}
                dataSize={dataSize}
                filters={filters}
                setFilters={setFilters}
                symbols={symbols}
              />
            )}
          </Card>
        </div>

        <div
          className={`w-full lg:w-[100%] 2xl:w-[85%] flex overflow-auto sm:h-[70vh]   flex-col sm:flex-row justify-between  mb-[5%] ${
            selectedTab == "traders" ? "flex" : "hidden"
          }`}
        >
          <AuthorsData />
        </div>
      </div>
    </div>
  );
}
const MetaTags = () => {
  const router = usePathname();
  let [currentPath, setCurrentPath] = useState("");
  let [symbol, seySymbol] = useState("");
  const { t } = useTranslation("marketplace-page");
  useEffect(() => {
    setCurrentPath(router);
    seySymbol(router.split("/")[2]);
  }, [router]);

  return currentPath == "/marketplace" ? (
    <Meta title={t("Marketplace")} description={t("Marketplace")} />
  ) : (
    <Meta
      title={symbol + " " + t("Marketplace")}
      description={t("Marketplace") + symbol + "." + " " + t("Marketplace")}
    />
  );
};

let MarketplaceFinal = () => {
  const { darkTheme } = useDarkTheme();
  const { user } = useUserContext();

  return (
    <div className="w-full h-full">
      <MetaTags />
      {user ? (
        <Layout
          meta={""}
          footer={""}
          trainingComponent={""}
          IdeasStream={""}
          hideRiskWarning
          hideSpaceWhenNoRiskWarning
          hideIdeastream
          onScrollBg
        >
          <Marketplace />
        </Layout>
      ) : (
        <div className="">
          <Header />
          <Marketplace />
          <Footer darkTheme={darkTheme} />
        </div>
      )}
    </div>
  );
};

export const getServerSideProps = async ({ params, locale }: any) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || "en", ["broker-page", 
        "marketplace-page",
        "layout-page",
        "build-page",
        "common-page",
        "test-page",
        "add-ons-form",
        "translation"
      ])),
    },
  };
};

export default MarketplaceFinal;
